import { computed, type ComputedRef } from 'vue';

interface HeaderCustomisation {
  backgroundColor: ComputedRef<string | undefined>;
  isVisible: ComputedRef<boolean>;
}

interface TitleCustomisation {
  textContent: ComputedRef<string | undefined>;
  textColor: ComputedRef<string | undefined>;
  isVisible: ComputedRef<boolean>;
}

interface SearchCustomisation {
  placeholderText: ComputedRef<string | undefined>;
  placeholderColor?: ComputedRef<string | undefined>;
  actionColor?: ComputedRef<string | undefined>;
  isVisible: ComputedRef<boolean>;
}

interface NavIconCustomisation {
  actionColor: ComputedRef<string | undefined>;
  isVisible: ComputedRef<boolean>;
}

interface HeaderPropsOptions {
  header: HeaderCustomisation;
  title: TitleCustomisation;
  search?: SearchCustomisation;
  navIcon1?: NavIconCustomisation;
  navIcon2?: NavIconCustomisation;
}

/**
 * Composable to build header props for GenericHeader component
 * Centralizes the logic for transforming customisation objects into header props
 */
export const useHeaderProps = (options: HeaderPropsOptions) => {
  const headerProps = computed(() => {
    const props: {
      header?: {
        backgroundColor?: string;
        isVisible?: boolean;
      };
      title?: {
        textContent?: string;
        textColor?: string;
        isVisible?: boolean;
      };
      search?: {
        placeholderText?: string;
        placeholderColor?: string;
        actionColor?: string;
        isVisible?: boolean;
      };
      navIcon1?: {
        actionColor?: string;
        isVisible?: boolean;
      };
      navIcon2?: {
        actionColor?: string;
        isVisible?: boolean;
      };
    } = {
      header: {
        backgroundColor: options.header.backgroundColor.value,
        isVisible: options.header.isVisible.value,
      },
      title: {
        textContent: options.title.textContent.value,
        textColor: options.title.textColor.value,
        isVisible: options.title.isVisible.value,
      },
    };

    if (options.search) {
      props.search = {
        placeholderText: options.search.placeholderText.value,
        placeholderColor: options.search.placeholderColor?.value,
        actionColor: options.search.actionColor?.value,
        isVisible: options.search.isVisible.value,
      };
    }

    if (options.navIcon1) {
      props.navIcon1 = {
        actionColor: options.navIcon1.actionColor.value,
        isVisible: options.navIcon1.isVisible.value,
      };
    }

    if (options.navIcon2) {
      props.navIcon2 = {
        actionColor: options.navIcon2.actionColor.value,
        isVisible: options.navIcon2.isVisible.value,
      };
    }

    return props;
  });

  return { headerProps };
};
